import type { Ring, Vec2, Vec3 } from '../../../geo/types';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { orientedFace, planPoint } from '../solids';
import { APSE_SIDE, CHURCH_PLAN, type VesselPlan } from './churchModel';
import {
  buttress,
  centredWindow,
  gableWall,
  pointedWindows,
  rectWall,
  roofUv,
  type MasonryStyles,
} from './masonry';

export interface WindowZone {
  readonly sill: number;
  readonly top: number;
}

export interface VesselOptions {
  /** Window band on the long walls; null where aisles or side vessels hide them. */
  readonly longWindows: WindowZone | null;
  readonly westWindow: boolean;
  readonly bay: number;
}

export const VESSEL = {
  apseSill: 3,
  apseWindowShare: 0.55,
  apseWindowDrop: 1.2,
  westSill: 3,
  westWindowShare: 0.5,
  /** Share of the gable height the west window reaches into. */
  westWindowReach: 0.35,
  buttress: { width: 1, depth: CHURCH_PLAN.buttressDepth, heightDrop: 1.5, pinnacle: 0 },
} as const;

/**
 * How far the apse buttress tips stand beyond the vessel's side walls and beyond its apse tip:
 * the octagon facets turn the buttresses 22.5° off both axes.
 */
export function apseButtressReach(): number {
  const { depth, width } = VESSEL.buttress;
  const tilt = Math.PI / 8;
  return depth * Math.cos(tilt) + (width / 2) * Math.sin(tilt);
}

export function vesselOutline(v: VesselPlan): Vec2[] {
  const { u0, chordU: c, vc, half: h } = v;
  if (v.apse === 'flat') {
    return [
      [u0, vc - h],
      [c, vc - h],
      [c, vc + h],
      [u0, vc + h],
    ];
  }
  const s = APSE_SIDE * h;
  return [
    [u0, vc - h],
    [c + s, vc - h],
    [c + h, vc - s],
    [c + h, vc + s],
    [c + s, vc + h],
    [u0, vc + h],
  ];
}

/** Grounded buttresses of the polygonal choir, using the same reach as its mesh. */
export function vesselButtressRings(v: VesselPlan): Ring[] {
  if (v.apse === 'flat') return [];
  return vesselOutline(v)
    .slice(1, -1)
    .map((at) => {
      const d: Vec2 = [at[0] - v.chordU, at[1] - v.vc],
        length = Math.hypot(...d),
        outward: Vec2 = [d[0] / length, d[1] / length],
        along: Vec2 = [-outward[1], outward[0]];
      const corner = (s: number, depth: number): Vec2 => [
        at[0] + (along[0] * s * VESSEL.buttress.width) / 2 + outward[0] * depth,
        at[1] + (along[1] * s * VESSEL.buttress.width) / 2 + outward[1] * depth,
      ];
      return [
        corner(-1, -0.1),
        corner(1, -0.1),
        corner(1, VESSEL.buttress.depth),
        corner(-1, VESSEL.buttress.depth),
      ];
    });
}

function edgeLength(a: Vec2, b: Vec2): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

function buildVesselWalls(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  vessel: VesselPlan,
  options: VesselOptions
): void {
  const ring = vesselOutline(vessel);
  const last = ring.length - 1;
  const gable = { eaveY: vessel.eaveY, apexY: vessel.ridgeY };
  ring.forEach((from, i) => {
    const to = ring[(i + 1) % ring.length]!;
    const len = edgeLength(from, to);
    const isLong = i === 0 || i === last - 1;
    if (i === last) {
      const top = vessel.eaveY + VESSEL.westWindowReach * (vessel.ridgeY - vessel.eaveY);
      const openings = options.westWindow
        ? centredWindow(len, VESSEL.westSill, top, VESSEL.westWindowShare)
        : [];
      gableWall(builder, styles, frame, [from, to], gable, openings);
    } else if (isLong) {
      const zone = options.longWindows;
      const openings = zone ? pointedWindows(len, options.bay, zone.sill, zone.top) : [];
      rectWall(builder, styles, frame, [from, to], [0, vessel.eaveY], openings);
    } else if (vessel.apse === 'flat') {
      const top = vessel.eaveY + VESSEL.westWindowReach * (vessel.ridgeY - vessel.eaveY);
      gableWall(
        builder,
        styles,
        frame,
        [from, to],
        gable,
        centredWindow(len, VESSEL.westSill, top, VESSEL.westWindowShare)
      );
    } else {
      const top = vessel.eaveY - VESSEL.apseWindowDrop;
      rectWall(
        builder,
        styles,
        frame,
        [from, to],
        [0, vessel.eaveY],
        centredWindow(len, VESSEL.apseSill, top, VESSEL.apseWindowShare)
      );
    }
  });
}

function slope(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  points: readonly Vec3[],
  outward: Vec3
): void {
  const uv = roofUv(points[0]!, points[1]!, points[points.length - 1]!);
  orientedFace(builder, styles.roof, frame, points, outward, uv);
}

export function buildVesselRoof(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  vessel: VesselPlan
): void {
  const ring = vesselOutline(vessel);
  const { u0, chordU: c, vc, eaveY, ridgeY } = vessel;
  const ridgeW = planPoint(u0, ridgeY, vc);
  const ridgeE = planPoint(c, ridgeY, vc);
  const at = (p: Vec2): Vec3 => planPoint(p[0], eaveY, p[1]);
  const last = ring.length - 1;
  slope(builder, styles, frame, [at(ring[0]!), at(ring[1]!), ridgeE, ridgeW], [0, 1, 1]);
  slope(builder, styles, frame, [at(ring[last - 1]!), at(ring[last]!), ridgeW, ridgeE], [0, 1, -1]);
  if (vessel.apse === 'flat') return;
  for (let i = 1; i < last - 1; i++) {
    const a = ring[i]!;
    const b = ring[i + 1]!;
    const n: Vec2 = [b[1] - a[1], -(b[0] - a[0])];
    slope(builder, styles, frame, [at(a), at(b), ridgeE], [n[0], 1, -n[1]]);
  }
}

function buildApseButtresses(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  vessel: VesselPlan
): void {
  if (vessel.apse === 'flat') return;
  const ring = vesselOutline(vessel);
  const spec = { ...VESSEL.buttress, height: vessel.eaveY - VESSEL.buttress.heightDrop };
  for (let i = 1; i <= ring.length - 2; i++) {
    const p = ring[i]!;
    const d: Vec2 = [p[0] - vessel.chordU, p[1] - vessel.vc];
    const len = Math.hypot(d[0], d[1]);
    buttress(builder, styles, frame, p, [d[0] / len, d[1] / len], spec);
  }
}

/** Walls, windows, gable roof and polygonal apse of a church's central vessel. */
export function buildVessel(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  vessel: VesselPlan,
  options: VesselOptions
): void {
  buildVesselWalls(builder, styles, frame, vessel, options);
  buildVesselRoof(builder, styles, frame, vessel);
  buildApseButtresses(builder, styles, frame, vessel);
}
